import React, { FormEvent, useEffect, useState } from 'react'
import { Alert, Button, IconButton, InputLabel, TextField } from '@mui/material';
import { Form } from 'react-router-dom';

import { ICustomer, ICustomerLocation } from '../../types/customer';

import { CustomerService } from '../../services/customerService';

import { Icons } from '../icons';
import Json from '../json';

import '../../styles/customerLocationsForm.scss';
import { UtilityService } from '../../services/utilityService';

export interface ICustomerLocationsFormProps {
	customer: ICustomer;
	refreshCallback: () => void;
}

const CustomerLocationsForm: React.FunctionComponent<ICustomerLocationsFormProps> = ({ customer, refreshCallback }) => {
	const [locationView, setLocationView] = useState<ICustomerLocation[]>([...customer.locations ?? []]);
	const [formFields, setFormFields] = useState<ICustomerLocation>({
		id: undefined,
		customerId: customer ? Number.parseInt(customer.id) : 0,
		locationName: '',
		isActive: true
	});
	const [isAdding, setIsAdding] = useState(false);
	const [isDeactivatingIds, setIsDeactivatingIds] = useState<number[]>([]);
	const [error, setError] = useState<string | undefined>();

	useEffect(() => {		
		setLocationView([...customer.locations ?? []].sort((a, b) => UtilityService.compareStrings(a.locationName, b.locationName)));
	}, [customer]);

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setIsAdding(true);
		setError(undefined);

		if (locationView
			.some(l =>
				l.locationName.toLowerCase().trim() == formFields.locationName.toLowerCase().trim() &&
				l.isActive)) {
			setError("A customer location with this name already exists.");
			setIsAdding(false);
			return;
		}

		CustomerService.addLocation(formFields)
			.then((response) => {
				console.log('CustomerService.addLocation: SUCCESS', response);
				setFormFields({
					id: undefined,
					locationName: "",
					customerId: Number.parseInt(customer.id)
				});
				setLocationView([...locationView, response.data].sort((a, b) => UtilityService.compareStrings(a.locationName, b.locationName)));
				refreshCallback();
			})
			.catch((error) => {
				console.log('CustomerService.addLocation: ERROR', error);
				setError("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsAdding(false);
			});
	}

	const handleRemoveLocation = (id: number | undefined) => {
		if (id) {
			setError(undefined);
			setIsDeactivatingIds(isDeactivatingIds => [...isDeactivatingIds, id]);

			CustomerService.deactivateLocation(id)
				.then((response) => {
					console.log('CustomerService.deactivateLocation: SUCCESS', response);
					setLocationView(locationView.filter(c => c.id != id));
					refreshCallback();
				})
				.catch((error) => {
					console.log('CustomerService.deactivateLocation: ERROR', error);
					setError("We encountered an error while processing your request. Please try again.");
				})
				.finally(() => {
					setIsDeactivatingIds(isDeactivatingIds.filter((item) => { return item != id }));
				});
		}
	}

	const isDeactivateDisabled = (id: number | undefined) => {
		if (id && isDeactivatingIds.indexOf(id) >= 0) {
			return true;
		}
		return false;
	}

	return (
		<section className="hwi-customer-locations-form">
			<div className="results-table">
				<div className="headers">
					<div className="flex-5">
						<p>
							Location Name
						</p>
					</div>
					<div className="flex-1">{""}</div>
				</div>
				<div className="data">
					{locationView && locationView.filter((item) => item.isActive).length > 0 ?
						(
							locationView.filter((item) => item.isActive).map((item, index) => (
								<div className="row" key={index}>
									<div className="flex-5"><p>{item.locationName ?? 'N/A'}</p></div>
									<div className="controls flex-1">
										<p>
											<Button onClick={() => handleRemoveLocation(item.id)} disabled={isAdding} >
												{isDeactivateDisabled(item.id) ? "Deactivating..." : (
													<>
														<Icons.RemoveItem fill="#a82525" height="16px"></Icons.RemoveItem>
														<span>Deactivate</span>
													</>
												)}
											</Button>
										</p>
									</div>
								</div>
							))
						) : (<div className="no-results">0 results</div>)}
				</div>
				{error &&
					<Alert className="alert-error" severity="error" action={<IconButton onClick={() => { setError(undefined); }} size='small'>X</IconButton>}>{error}</Alert>
				}
				<Form onSubmit={(event) => handleSubmit(event)}>
					<div className="row">
						<div>
						<InputLabel>Location Name</InputLabel>
							<TextField className="location-name-field" name="name" required placeholder='Enter location name' variant="outlined" value={formFields.locationName} size="small" onChange={(event) => { setFormFields({ ...formFields, ["locationName"]: event.target.value }) }}></TextField>
						</div>
						<div className="controls">
							<p>
								<Button type="submit" className="save-button" disabled={isAdding || !formFields.locationName} variant="text">
									{isAdding ? "Saving..." : (
										<>
											<Icons.AddItem fill="#434D76" height="16px"></Icons.AddItem>
											<span>Add Location</span>
										</>
									)}
								</Button>
							</p>
						</div>
					</div>
				</Form>
			</div>
		</section>
	)
}

export default CustomerLocationsForm;

